import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

/**
 * MultiSelectDropdown — wiederverwendbares Mehrfachauswahl-Dropdown im Lagerbestand-Stil
 * (Chip-Button + Badge + Checkbox-Liste, Click-outside, optional durchsuchbar).
 * Ersetzt das in MaterialFilters/AdvancedFilters inline duplizierte Pattern.
 *
 * @param {string} label        Chip-Beschriftung
 * @param {Array<string|{value,label}>} options
 * @param {string[]} selected   ausgewählte values
 * @param {(next:string[])=>void} onChange
 * @param {boolean} [searchable]
 * @param {string} [width]      Tailwind-Breite des Dropdowns (default w-56)
 */
const MultiSelectDropdown = ({ label, options, selected, onChange, searchable = false, width = 'w-56' }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const norm = (options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const shown = q ? norm.filter((o) => String(o.label).toLowerCase().includes(q.toLowerCase())) : norm;
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  const count = selected.length;

  const chipBase = 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none';
  const chipActive = isDarkMode ? 'bg-brand/25 border-brand/50 text-blue-200' : 'bg-brand/10 border-brand/40 text-brand';
  const chipInactive = isDarkMode ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12' : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} className={`${chipBase} ${count > 0 ? chipActive : chipInactive}`}>
        <span>{label}</span>
        {count > 0 && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-white' : 'bg-brand text-white'}`}>{count}</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute z-40 mt-1 ${width} rounded-xl shadow-lg border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-white/15' : 'bg-white border-slate-200'}`}>
          {searchable && (
            <div className={`p-2 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              <div className="relative">
                <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('lm.banfSearchOpt')}
                  className={`w-full pl-7 pr-2 py-1.5 text-xs rounded-lg border outline-none ${isDarkMode ? 'bg-white/8 border-white/15 text-white placeholder-white/30' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`} />
              </div>
            </div>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {shown.length === 0 ? (
              <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>—</div>
            ) : shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button key={o.value} type="button" onClick={() => toggle(o.value)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left ${isDarkMode ? 'hover:bg-white/8 text-white/80' : 'hover:bg-slate-50 text-slate-700'}`}>
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-brand border-brand' : (isDarkMode ? 'border-white/30' : 'border-slate-300')}`}>
                    {on && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
          {count > 0 && (
            <button type="button" onClick={() => onChange([])}
              className={`w-full text-center py-1.5 text-[11px] border-t ${isDarkMode ? 'border-white/10 text-white/50 hover:text-white/80' : 'border-slate-100 text-slate-400 hover:text-slate-600'}`}>
              {t('lm.banfReset')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
